import {
  AccessoryGroup,
  CircuitType,
  EnergySource,
  EnergySourceType,
  FlowTempsType,
  FuelType,
  GroundType,
  HeatPumpStat,
  ReferenceFuelType
} from '@/helper/Types'
import { api } from '@/services/ApiService'
import { ConfigurationResponse } from '@/services/models/ConfigurationResponse'
import BrandMapping from '@/helper/BrandMapping'
import i18n from '@/helper/Translations'
import appStore from '@/store/AppStore'
import { reactive } from 'vue'
import { ProjectOverviewResponse } from '@/services/models/ProjectOverviewResponse'

class UserStore {

  private _token = ''
  private _forcedLocale = ''
  private _forcedBrand = ''
  private _forcedCountry = ''
  private _companyIdSuffix = ''
  private _showTranslationKeys = false
  private _planningSuiteToken = ''

  private _availableLanguages: string[] = []
  private _brand = ''
  private _subBrand = ''
  private _constructionYears: string[] = []
  private _coolingIndoorTemperatures: number[] = []
  private _coolingVentilationTypes: string[] = []
  private _country = ''
  private _dhwValues: number[] = []
  private _dhwMixingLoss: number = 1
  private _email = ''
  private _contactId = ''
  private _companyId = ''
  private _isPrivileged = false

  private _enableSimulation = true
  private _enableAccessoryShortList = false
  private _enableCityDropdown = false
  private _enableCoolingPage = false
  private _enableCoolingLoadCalculation = false
  private _enableCoolingLoadCalculationInfo = true
  private _enableHeatingLoadCalculation = false
  private _enableHeatingLoadCalculationInfo = true
  private _enableHeatingConsumption = false
  private _enableHeatingThermalEnvelope = false
  private _backupHeaterOptional = false
  private _enableHybridSystem = false
  private _enableRequestSystemScheme = false
  private _minSystemSchemeScorePercent = 0
  private _enableCustomerAddress = false
  private _enablePdfFooterText = false
  private _enableCustomPrices = false
  private _enableShowPricesInReportOption = false
  private _enableLivingArea = false
  private _enablePdfSubsidiesPage = false
  private _showOperatingPoints = false
  private _enableHeatingCostComparison = false
  private _enableAltitudeInput = false
  private _enableHeatpumpVoltageFilter = false
  private _enableHeatPumpCascades = false
  private _enableIrregularCurrency = false
  private _enableBufferSeparation = true
  private _enableBufferMinVolumeHint = false
  private _enableExtendedNoiseCalculation = true
  private _enableDisplayOfVatPrices = true
  private _electricityPrice = 0
  private _referenceFuelTypes: ReferenceFuelType[] = []
  private _energySources!: EnergySourceType[]
  private _groundTypes?: GroundType[]
  private _globalLinks: { [key: string]: string } = {}
  private _productAvailabilityLinks: { [key: string]: string } = {}
  private _heatingIndoorTemperatures: number[] = []
  private _heatingVentilationTypes: string[] = []
  private _heatingWindowTypes: string[] = []
  private _heatingAdjoinedSideTemperature: number = 0
  private _heatingWindowSurfaceFactor: number = 0
  private _buildingAreaRatio: number = 1
  private _heatPumpPlacement = false
  private _hidePrices = false
  private _isoLocale!: string
  private _isConfigurationLoaded = false
  private _name = ''
  private _nationalNoiseLimitation = 0
  private _bufferTankOptional = false
  private _projectPostalCodes: string[] = []
  private _customerPostalCodes: string[] = []
  private _postalCodeValidationPattern!: string | null
  private _systemTypes: Record<string, Record<string, number[] | number>> = {}
  private _windowOrientations: string[] = []
  private _fuelTypes: FuelType[] = []
  private _enableCooling = false
  private _feedbackSurveyLink!: string | null
  private _hybridKitInfoLink!: string | null
  private _feedbackSurveyFrequency = 0
  private _realm!: string
  private _vatRate?: number
  private _heatLossExternalLinkUrl = ''
  private _heatPumpStatsConfig: HeatPumpStat[] = []
  private _preselectHeatPumpVoltageFilter230V = false
  private _preselectHeatPumpVoltageFilter400V = false
  private _preselectExistingBackupHeaterCombiBoiler = false
  private _accessoryGroups: AccessoryGroup[] = []

  private _heatPumpSuggestionHybridSourceTemperature?: number
  private _heatPumpSuggestionHybridFlowTemperature?: number
  private _enableBundleSelection = false

  constructor() {
    this.loadFromLocalStorage()
  }

  get vatRate() {
    return this._vatRate
  }

  get heatPumpStatsConfig() {
    return this._heatPumpStatsConfig
  }

  get enableAccessoryShortList(): boolean {
    return this._enableAccessoryShortList
  }

  get enableIrregularCurrency(): boolean {
    return this._enableIrregularCurrency
  }

  get enableBufferSeparation(): boolean {
    return this._enableBufferSeparation
  }

  get enableBufferMinVolumeHint(): boolean {
    return this._enableBufferMinVolumeHint
  }

  get enableExtendedNoiseCalculation(): boolean {
    return this._enableExtendedNoiseCalculation
  }

  get referenceFuelTypes(): ReferenceFuelType[] {
    return this._referenceFuelTypes || []
  }

  get preselectExistingBackupHeaterCombiBoiler(): boolean {
    return this._preselectExistingBackupHeaterCombiBoiler
  }

  get preselectHeatPumpVoltageFilter400V(): boolean {
    return this._preselectHeatPumpVoltageFilter400V
  }

  get preselectHeatPumpVoltageFilter230V(): boolean {
    return this._preselectHeatPumpVoltageFilter230V
  }

  get accessoryGroups(): AccessoryGroup[] {
    return this._accessoryGroups
  }

  getCalorificValue(fuelType: string, unit: string): number | undefined {
    const calorificValue = this._referenceFuelTypes
      .filter(r => r.fuelType === fuelType)
      .flatMap(f => f.calorificValues)
      .find(c => c.unit === unit)

    return calorificValue?.calorificValue
  }

  getCalorificValues(fuelType: string): string[] {
    return this._referenceFuelTypes
      .filter(r => r.fuelType === fuelType)
      .flatMap(f => f.calorificValues)
      .map(cv => cv.unit)
  }

  get activeFuelTypeOptions(): string[] {
    return this._referenceFuelTypes.map(type => type.fuelType)
  }

  get electricityPrice(): number {
    return this._electricityPrice
  }

  get enableCooling(): boolean {
    return this._enableCooling
  }

  get enableBundleSelection(): boolean {
    return this._enableBundleSelection
  }

  get enableAltitudeInput(): boolean {
    return this._enableAltitudeInput
  }

  get enableHeatpumpVoltageFilter(): boolean {
    return this._enableHeatpumpVoltageFilter
  }

  get systemTypes(): Record<string, Record<string, number[] | number>> {
    return this._systemTypes
  }

  get fuelTypes(): FuelType[] {
    return this._fuelTypes
  }

  get currentLanguage(): string {
    return i18n.global.locale
  }

  set currentLanguage(lang: string) {
    lang = this._availableLanguages.includes(lang) ? lang : this._availableLanguages[0]
    window.localStorage.setItem('preferred-lang', lang)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    i18n.global.locale = lang
  }

  get enableCustomerAddress(): boolean {
    return this._enableCustomerAddress
  }

  get enableLivingArea(): boolean {
    return this._enableLivingArea
  }

  get feedbackSurveyLink(): string | null {
    return this._feedbackSurveyLink
  }

  get feedbackSurveyFrequency(): number {
    return this._feedbackSurveyFrequency
  }

  get hybridKitInfoLink(): string | null {
    return this._hybridKitInfoLink
  }

  get availableLanguages(): string[] {
    return this._availableLanguages
  }

  get token(): string {
    return this._token
  }

  set token(value: string) {
    this._token = value
    this.saveToLocalStore()
  }

  get hasToken(): boolean {
    return this._token.trim().length > 0
  }

  get currentIsoLocale(): string {
    if (!this._availableLanguages.length) {
      return (this._forcedLocale ? this._forcedLocale : i18n.global.locale).replace('_', '-')
    }

    const country = (this._forcedLocale.length > 4 ? this._forcedLocale.substr(3, 2) : this._country).toUpperCase()

    const lang = i18n.global.locale.substr(0, 2).toLowerCase()

    return country === 'XX' ? 'xx-XX' : `${lang}-${country}`
  }

  get isPrivileged(): boolean {
    return this._isPrivileged
  }

  get forcedBrand(): string {
    return this._forcedBrand
  }

  set forcedBrand(value: string) {
    this._forcedBrand = value
    this.saveToLocalStore()
  }

  get forcedCountry(): string {
    return this._forcedCountry
  }

  set forcedCountry(value: string) {
    this._forcedCountry = value
    this.saveToLocalStore()
  }

  get companyIdSuffix(): string {
    return this._companyIdSuffix
  }

  set companyIdSuffix(value: string) {
    this._companyIdSuffix = value
    this.saveToLocalStore()
  }

  get showTranslationKeys(): boolean {
    return this._showTranslationKeys
  }

  set showTranslationKeys(value: boolean) {
    this._showTranslationKeys = value
  }

  get isoLocale(): string {
    return this._isoLocale
  }

  get globalLinks(): { [p: string]: string } {
    return this._globalLinks
  }

  get productAvailabilityLinks(): { [p: string]: string } {
    return this._productAvailabilityLinks
  }

  get postalCodeValidationPattern(): string | null {
    return this._postalCodeValidationPattern
  }

  get validProjectPostalCodes(): string[] {
    return this._projectPostalCodes
  }

  set validProjectPostalCodes(value: string[]) {
    this._projectPostalCodes = value
  }

  get validCustomerPostalCodes(): string[] {
    return this._customerPostalCodes
  }

  set validCustomerPostalCodes(value: string[]) {
    this._customerPostalCodes = value
  }

  get enableCityDropdown(): boolean {
    return this._enableCityDropdown
  }

  get enableRequestSystemScheme(): boolean {
    return this._enableRequestSystemScheme
  }

  get minSystemSchemeScorePercent(): number {
    return this._minSystemSchemeScorePercent
  }

  get enableCoolingLoadCalculation(): boolean {
    return this._enableCoolingLoadCalculation
  }

  get enableHeatingLoadCalculation(): boolean {
    return this._enableHeatingLoadCalculation
  }

  get enableHybridSystem(): boolean {
    return this._enableHybridSystem
  }

  get enablePdfFooterText(): boolean {
    return this._enablePdfFooterText
  }

  get enableDisplayOfVatPrices(): boolean {
    return this._enableDisplayOfVatPrices
  }

  get enableCustomPrices(): boolean {
    return this._enableCustomPrices
  }

  get enableShowPricesInReportOption(): boolean {
    return this._enableShowPricesInReportOption
  }

  get enablePdfSubsidiesPage(): boolean {
    return this._enablePdfSubsidiesPage
  }

  get showOperatingPoints(): boolean {
    return this._showOperatingPoints
  }

  get enableHeatingCostComparison(): boolean {
    return this._enableHeatingCostComparison
  }

  get energySources(): EnergySourceType[] {
    return this._energySources
  }

  get dhwValues(): number[] {
    return this._dhwValues
  }

  get dhwMixingLoss(): number {
    return this._dhwMixingLoss
  }

  get groundTypes(): GroundType[] | undefined {
    return this._groundTypes
  }

  get isConfigurationLoaded(): boolean {
    return this._isConfigurationLoaded
  }

  get country(): string {
    return this._country
  }

  get brand(): string {
    return this._brand
  }

  get subBrand(): string {
    return this._subBrand
  }

  get name(): string {
    return this._name
  }

  get email(): string {
    return this._email
  }

  get contactId(): string {
    return this._contactId
  }

  get companyId(): string {
    return this._companyId
  }

  get themeClass(): string {
    return this.brand ? 'theme-' + BrandMapping.getBrandShortHand(this.brand) : ''
  }

  get bufferTankOptional(): boolean {
    return this._bufferTankOptional
  }

  get heatPumpPlacement(): boolean {
    return this._heatPumpPlacement
  }

  get nationalNoiseLimitation(): number {
    return this._nationalNoiseLimitation
  }

  get coolingVentilationTypes(): string[] {
    return this._coolingVentilationTypes
  }

  get windowOrientations(): string[] {
    return this._windowOrientations
  }

  get heatingVentilationTypes(): string[] {
    return this._heatingVentilationTypes
  }

  get heatingIndoorTemperatures(): number[] {
    return this._heatingIndoorTemperatures
  }

  get coolingIndoorTemperatures(): number[] {
    return this._coolingIndoorTemperatures
  }

  get constructionYears(): string[] {
    return this._constructionYears
  }

  get heatingWindowTypes(): string[] {
    return this._heatingWindowTypes
  }

  get hidePrices(): boolean {
    return this._hidePrices
  }

  get enableHeatingLoadCalculationInfo(): boolean {
    return this._enableHeatingLoadCalculationInfo
  }

  get enableHeatingConsumption(): boolean {
    return this._enableHeatingConsumption
  }

  get enableHeatingThermalEnvelope(): boolean {
    return this._enableHeatingThermalEnvelope
  }

  get enableCoolingLoadCalculationInfo(): boolean {
    return this._enableCoolingLoadCalculationInfo
  }

  get enableSimulation(): boolean {
    return this._enableSimulation
  }

  get backupHeaterOptional(): boolean {
    return this._backupHeaterOptional
  }

  get realm(): string {
    return this._realm
  }

  set realm(value: string) {
    this._realm = value
    this.saveToLocalStore()
  }

  get heatLossExternalLinkUrl(): string {
    return this._heatLossExternalLinkUrl
  }

  get heatPumpSuggestionHybridSourceTemperature(): number | undefined {
    return this._heatPumpSuggestionHybridSourceTemperature
  }

  get heatPumpSuggestionHybridFlowTemperature(): number | undefined {
    return this._heatPumpSuggestionHybridFlowTemperature
  }

  get planningSuiteToken(): string {
    return this._planningSuiteToken
  }

  set planningSuiteToken(value: string) {
    this._planningSuiteToken = value
    this.saveToLocalStore()
  }

  public getCoolingFlowTemperature(selCircuitType: CircuitType) {
    const systemType = this._systemTypes[selCircuitType]

    if (!systemType || !Object.keys(systemType).includes(FlowTempsType.COOLING)) {
      return undefined
    }

    return systemType[FlowTempsType.COOLING]
  }

  public getFlowTemps(selCircuitType: CircuitType | undefined = undefined): number[] {
    const flowTemperatures: number[] = []

    if (selCircuitType) {
      return (this._systemTypes[selCircuitType][FlowTempsType.HEATING] as number[]) || []
    }

    Object.keys(this._systemTypes).forEach(circuitType =>
      flowTemperatures.push(...(this._systemTypes[circuitType][FlowTempsType.HEATING] as number[]))
    )

    return [...new Set(flowTemperatures)]
  }

  public getSystemTypes(forCooling = false): CircuitType[] {
    return Object.keys(this._systemTypes).filter(
      key => this._systemTypes[key][forCooling ? FlowTempsType.COOLING : FlowTempsType.HEATING] !== undefined
    ) as CircuitType[]
  }

  public setConfig(data: ConfigurationResponse) {
    this._contactId = data.identity.contact.contactId
    this._companyId = data.identity.account.companyId
    this._name = `${data.identity.contact.givenName} ${data.identity.contact.surname}`
    this._email = data.identity.contact.email
    this._isPrivileged = data.identity.privileged || false
    this._brand = data.configurations.brand
    this._subBrand = data.identity.account.brand.toUpperCase()
    this._country = data.identity.account.countryCode.toUpperCase()
    this._isConfigurationLoaded = true
    this._dhwValues = data.configurations.dhwValues
    this._dhwMixingLoss = data.configurations.dhwMixingLoss || 1

    this._electricityPrice = data.configurations.systemElectricity.price || 0
    this._referenceFuelTypes =
      data.configurations.referenceFuelTypes?.filter(f => f.fuelType !== FuelType.ELECTRICITY) || []

    this._energySources = data.configurations.energySources.filter(e =>
      Object.values(EnergySource).includes(e.name as EnergySource)
    )
    this._groundTypes = data.configurations.groundTypes
    this._enableCityDropdown = data.configurations.enableCityDropdown || false
    this._enableRequestSystemScheme = data.configurations.enableRequestSystemScheme || false
    this._minSystemSchemeScorePercent = data.configurations.minSystemSchemeScorePercent
    this._enablePdfFooterText = data.configurations.enablePdfFooterText || false
    this._enableCustomPrices = data.configurations.enableCustomPrices || false
    this._enableShowPricesInReportOption = data.configurations.enableShowPricesInReportOption || false
    this._enablePdfSubsidiesPage = data.configurations.enablePdfSubsidiesPage || false
    this._showOperatingPoints = data.configurations.showOperatingPoints || false
    this._enableHeatingCostComparison = data.configurations.enableHeatingCostComparison || false
    this._enableCoolingPage = data.configurations.enableCoolingPage || false
    this._enableAltitudeInput = data.configurations.enableAltitudeInput || false
    this._enableHeatpumpVoltageFilter = data.configurations.enableHeatpumpVoltageFilter || false
    this._enableIrregularCurrency = data.configurations.enableIrregularCurrency || false
    this._enableHeatPumpCascades = data.configurations.enableHeatPumpCascades || false
    this._enableBufferSeparation = data.configurations.enableBufferSeparation || false
    this._enableBufferMinVolumeHint = data.configurations.enableBufferMinVolumeHint || false
    this._enableExtendedNoiseCalculation = data.configurations.enableExtendedNoiseCalculation || false
    this._enableDisplayOfVatPrices = data.configurations.enableDisplayOfVatPrices || false
    this._systemTypes = data.configurations.systemTypes.reduce((acc, curr) => {
      acc[curr.systemType] = {}
      Object.assign(acc[curr.systemType], curr)
      return acc
    }, {} as Record<string, Record<string, number[] | number>>)
    this._globalLinks = data.configurations.globalLinks
    this._productAvailabilityLinks = data.configurations.productAvailabilityLinks
    this._isoLocale = data.identity.language.replace('_', '-')
    this._postalCodeValidationPattern = data.configurations.postalCodeValidationPattern
    this._bufferTankOptional = data.configurations.bufferTankOptional
    this._heatPumpPlacement = data.configurations.enableHeatPumpPlacement
    this._nationalNoiseLimitation = data.configurations.nationalNoiseLimitation
    this._constructionYears = (data.configurations?.constructionYears || []).map(e => e.key)

    this._enableCoolingLoadCalculation = data.configurations.enableCoolingLoadCalculation || false
    this._coolingIndoorTemperatures = data.configurations.coolingLoadCalculation?.indoorTemperatures || []
    this._windowOrientations = (data.configurations.coolingLoadCalculation?.windowOrientations || []).map(
      e => e.windowOrientation
    )
    this._coolingVentilationTypes = (data.configurations.coolingLoadCalculation?.ventilationTypes || []).map(
      e => e.ventilationType
    )

    this._enableCooling = data.configurations.enableCooling || false
    this._enableHeatingLoadCalculation = data.configurations.enableHeatingLoadCalculation || false
    this._heatingIndoorTemperatures = data.configurations.heatingLoadCalculation?.indoorTemperatures || []
    this._heatingVentilationTypes = (data.configurations.heatingLoadCalculation?.ventilationTypes || []).map(
      e => e.ventilationType
    )
    this._heatingWindowTypes = (data.configurations.heatingLoadCalculation?.windowTypes || []).map(e => e.windowType)
    this._heatingAdjoinedSideTemperature = data.configurations.heatingLoadCalculation?.adjoinedSideTemperature
    this._heatingWindowSurfaceFactor = data.configurations.heatingLoadCalculation?.windowSurfaceFactor
    this._buildingAreaRatio = data.configurations.buildingAreaRatio

    this._hidePrices = data.configurations.hidePrices
    this._enableHeatingLoadCalculationInfo = data.configurations.enableHeatingLoadCalculationInfo
    this._enableHeatingConsumption = data.configurations.enableHeatingLoadConsumptionCalculation
    this._enableHeatingThermalEnvelope = data.configurations.enableHeatingLoadThermalEnvelopeCalculation
    this._enableCoolingLoadCalculationInfo = data.configurations.enableCoolingLoadCalculationInfo
    this._backupHeaterOptional = data.configurations.backupHeaterOptional || false
    this._enableHybridSystem = data.configurations.enableHybridSystem || false
    if (this._enableHybridSystem) {
      this._fuelTypes = (data.configurations.hybridSystem?.fuelTypes || []).map(e => e.fuelType) as FuelType[]
    }
    this._enableCustomerAddress = data.configurations.enableCustomerAddress
    this._enableLivingArea = data.configurations.enableLivingArea
    this._enableSimulation = data.configurations.enableSimulation
    this._enableAccessoryShortList = data.configurations.enableAccessoryShortList
    this._vatRate = data.configurations.vatRate
    this._heatLossExternalLinkUrl = data.configurations.heatLossExternalLinkUrl
    this._heatPumpStatsConfig = data.configurations.heatPumpStats?.map(stat => stat.heatPumpStats as HeatPumpStat) || []

    this._preselectHeatPumpVoltageFilter230V = data.configurations.preselectHeatPumpVoltageFilter230V || false
    this._preselectHeatPumpVoltageFilter400V = data.configurations.preselectHeatPumpVoltageFilter400V || false
    this._preselectExistingBackupHeaterCombiBoiler = data.configurations.preselectExistingBackupHeaterCombiBoiler || false
    this._accessoryGroups = data.configurations.accessoryGroups || []

    this._heatPumpSuggestionHybridSourceTemperature = data.configurations.heatPumpSuggestionHybridSourceTemperature
    this._heatPumpSuggestionHybridFlowTemperature = data.configurations.heatPumpSuggestionHybridFlowTemperature

    this._feedbackSurveyLink = data.configurations.feedbackSurveyLink
    this._feedbackSurveyFrequency = data.configurations.feedbackSurveyFrequency

    this._hybridKitInfoLink = data.configurations.hybridKitInfoLink

    this._enableBundleSelection = data.configurations.enableBundleSelection
  }

  public async loadProjects(
    page = 1,
    size = 15,
    sortBy = '',
    sortDirection = false,
    search = ''
  ): Promise<ProjectOverviewResponse | undefined> {
    if (!this.hasToken) {
      return
    }
    try {
      appStore.isLoading = true
      return await api.fetchProjects(page, size, sortBy, sortDirection, search)
    } catch (e) {
      window.console.error('Error in fetching projects:')
      throw Error((e as Error).message)
    } finally {
      appStore.isLoading = false
    }
  }

  public async loadConfig() {
    try {
      const res = await api.loadConfiguration()
      this.setConfig(res)

      const locale = res.identity.language.substring(0, 2).toLowerCase()

      for (const loc of Object.keys(res.translations)) {
        if (!this.showTranslationKeys) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          i18n.global.setLocaleMessage(loc, res.translations[loc])
        }
        this._availableLanguages.push(loc)
      }
      this._availableLanguages.sort()

      const prefLang = window.localStorage.getItem('preferred-lang') || ''

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      i18n.global.locale = this._availableLanguages.includes(prefLang) ? prefLang : locale
      i18n.global.silentTranslationWarn = false
    } finally {
      appStore.isLoading = false // Unsets the initial loading status of AppStore
    }
  }

  public saveToLocalStore() {
    window.localStorage.setItem('token', this._token)
    window.localStorage.setItem('realm', this._realm)
    window.sessionStorage.setItem('psIntegrationToken', this._planningSuiteToken)
    window.sessionStorage.setItem('brand', this._forcedBrand)
    window.sessionStorage.setItem('country', this._forcedCountry)
    window.sessionStorage.setItem('companyIdSuffix', this._companyIdSuffix)
  }

  public loadFromLocalStorage() {
    this._token = window.localStorage.getItem('token') || ''
    this._realm = window.localStorage.getItem('realm') || ''
    this._planningSuiteToken = window.sessionStorage.getItem(('psIntegrationToken')) || ''
    this._forcedBrand = window.sessionStorage.getItem('brand') || ''
    this._forcedCountry = window.sessionStorage.getItem('country') || ''
    this._companyIdSuffix = window.sessionStorage.getItem('companyIdSuffix') || ''
  }
}

const userStore = reactive(new UserStore())
export default userStore
